import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface User {
  id: number;
  name: string;
  email: string;
  memberTypeTitle: string;
  createdAt: Date;
  phone: string;
  is_active: boolean;
  enable_mcp: boolean;
  isVerifiedOffice: boolean;
  businessInfo: BusinessInfo;
  lastAdDate?: string;
  totalAds?: number;
  remainingAds?: number;
  memberTypeLabel?: string;
}

export interface UsersListInfo {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  isNext: boolean;
  previous: number;
  users: Array<User>;
  errors: unknown;
}

export interface BusinessInfo {
  name: string;
  website: string;
  whatsapp: string;
  instagram: string;
  twitter: string;
  registrationNumber: number;
  location: string;
  mapUrl: string;
  isActive: boolean;
}

export interface OfficesFilterListInfo {
  phone: string;
  email: string;
  memberType: string;
  behaviorFilter: string;
  monthsWithoutRenewal: number | string;
  daysWithoutAds: number | string;
}

export interface UsersFilterListInfo {
  phone: string;
  email: string;
  name: string;
  member_type: number;
}

@Module
export default class UsersModule extends VuexModule implements UsersListInfo {
  users = [] as Array<User>;
  appUsers = [] as Array<User>;
  officesFilter = {
    phone: '',
    email: '',
    memberType: 'all',
    behaviorFilter: '',
    monthsWithoutRenewal: '',
    daysWithoutAds: '',
  } as OfficesFilterListInfo;
  usersFilter = {} as UsersFilterListInfo;
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  previous = 0;
  isNext = true;
  errors = {};

  /**
   * Get users list
   * @returns Array<User>
   */
  get usersList(): Array<User> {
    if (this.users.length > 0) this.users[0]['page'] = this.currentPage;
    return this.users;
  }

  /**
   * Get users list
   * @returns Array<User>
   */
  get usersAllList(): Array<User> {
    return this.appUsers;
  }

  /**
   * Get total items
   * @returns number
   */
  get listUsersCount(): number {
    return this.totalItems;
  }

  get getOfficesFilter(): OfficesFilterListInfo {
    return this.officesFilter;
  }

  get getUsersFilter(): UsersFilterListInfo {
    return this.usersFilter;
  }

  @Mutation
  [Mutations.SET_USERS](data) {
    this.users = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_USER_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.SET_ALL_APP_USERS](data) {
    this.appUsers = data.data;
  }

  @Mutation
  [Mutations.SET_OFFICES_FILTER](obj) {
    if (obj.phone !== undefined) this.officesFilter.phone = String(obj.phone);
    if (obj.email !== undefined) this.officesFilter.email = String(obj.email);
    if (obj.memberType !== undefined)
      this.officesFilter.memberType = String(obj.memberType);
    if (obj.behaviorFilter !== undefined) {
      this.officesFilter.behaviorFilter = obj.behaviorFilter;
    }
    if (obj.monthsWithoutRenewal !== undefined)
      this.officesFilter.monthsWithoutRenewal = obj.monthsWithoutRenewal;
    if (obj.daysWithoutAds !== undefined)
      this.officesFilter.daysWithoutAds = obj.daysWithoutAds;
  }

  @Mutation
  [Mutations.SET_USERS_FILTER](obj) {
    this.usersFilter.phone = String(obj.phone);
    this.usersFilter.email = String(obj.email);
    this.usersFilter.name = String(obj.name);
    this.usersFilter.member_type = Number(obj.member_type);
  }

  @Mutation
  [Mutations.RESET_USERS_LIST]() {
    this.users = [] as Array<User>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
    this.officesFilter = {
      phone: '',
      email: '',
      memberType: 'all',
      behaviorFilter: '',
      monthsWithoutRenewal: '',
      daysWithoutAds: '',
    };
  }

  @Action
  [Actions.RESET_USERS_STORE]() {
    this.context.commit(Mutations.RESET_USERS_LIST);
  }

  @Action
  [Actions.UPDATE_OFFICE_FILTER](val) {
    this.context.commit(Mutations.SET_OFFICES_FILTER, val);
  }

  @Action
  [Actions.UPDATE_USERS_FILTER](val) {
    this.context.commit(Mutations.SET_USERS_FILTER, val);
  }

  @Action
  [Actions.UPDATE_USER](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('members', id, {})
      .then(() => {
        this.context.commit(Mutations.RESET_USERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_VERIFICATION](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('members/verification', id, {})
      .then(() => {
        this.context.commit(Mutations.RESET_USERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_USER_MCP](payload) {
    const { id, ...data } = payload;
    ApiService.setAuthorizationHeader();
    return ApiService.update('members', `${id}/mcp`, data)
      .then(() => {
        this.context.commit(Mutations.SET_ERROR, {});
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_USER_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_USER_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_USER](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('members', id);
  }

  @Action
  [Actions.GET_USERS]() {
    const params = {
      page: this.currentPage,
      phone: this.usersFilter.phone,
      email: this.usersFilter.email,
      name: this.usersFilter.name,
      member_type: this.usersFilter.member_type,
    };
    ApiService.setAuthorizationHeader();
    return ApiService.query('/members', { params })
      .then(({ data }) => {
        data.data = data.data.map((r) => {
          r.createdAt = new Date(r.createdAt)
            .toLocaleString('en-GB')
            .split(',')[0];
          return r;
        });
        this.context.commit(Mutations.SET_USERS, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.GET_OFFICES]() {
    const params: Record<string, unknown> = {
      page: this.currentPage,
    };

    // Add filters only if they have values
    if (this.officesFilter.phone) params.phone = this.officesFilter.phone;
    if (this.officesFilter.email) params.email = this.officesFilter.email;
    if (
      this.officesFilter.memberType &&
      this.officesFilter.memberType !== 'all'
    ) {
      params.memberType = this.officesFilter.memberType;
    }
    if (
      this.officesFilter.behaviorFilter &&
      this.officesFilter.behaviorFilter !== 'all'
    ) {
      params.behaviorFilter = this.officesFilter.behaviorFilter;
    }
    if (this.officesFilter.monthsWithoutRenewal) {
      params.monthsWithoutRenewal = this.officesFilter.monthsWithoutRenewal;
    }
    if (this.officesFilter.daysWithoutAds) {
      params.daysWithoutAds = this.officesFilter.daysWithoutAds;
    }

    ApiService.setAuthorizationHeader();
    return ApiService.query('/members/offices/list', { params })
      .then(({ data }) => {
        data.data = data.data.map((r) => {
          r.createdAt = new Date(r.createdAt)
            .toLocaleString('en-GB')
            .split(',')[0];
          return r;
        });
        this.context.commit(Mutations.SET_USERS, data);
        // Return data so component can access summary
        return data;
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.GET_OFFICE](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('members/offices', id);
  }

  @Action
  [Actions.CREATE_OFFICE](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('members/offices/create', data)
      .then(({ data }) => {
        this.context.commit(Mutations.RESET_USERS_LIST);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_OFFICE]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('members/offices', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_USERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.INVALIDATE_OFFICE]({
    id,
    sendWhatsApp,
  }: {
    id: string;
    sendWhatsApp: boolean;
  }) {
    ApiService.setAuthorizationHeader();
    return ApiService.post(
      `members/offices/invalidate?id=${id}&sendWhatsApp=${sendWhatsApp}`,
      {}
    )
      .then(() => {
        this.context.commit(Mutations.RESET_USERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPLOAD_OFFICE_LOGO]({ id, data }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(`/members/uploadLogo/${id}`, data)
      .then(({ data }) => {
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.GET_EMPLOYEES](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('members/offices/employees', id);
  }

  @Action
  [Actions.GET_ALL_APP_USERS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/members/all-users')
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_APP_USERS, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }
}
